package dairy.mynotes.repository

import dairy.mynotes.firebase.FirebaseService
import dev.gitlive.firebase.auth.FirebaseUser

/**
 * Repository for Firebase Authentication
 * Handles user registration, login, and logout
 */
class AuthRepository {
    private val auth get() = FirebaseService.auth

    /**
     * Register new user with email and password
     * @return Result with FirebaseUser on success or Exception on failure
     */
    suspend fun register(email: String, password: String): Result<FirebaseUser?> {
        return try {
            val result = auth.createUserWithEmailAndPassword(email, password)
            Result.success(result.user)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Login user with email and password
     * @return Result with FirebaseUser on success or Exception on failure
     */
    suspend fun login(email: String, password: String): Result<FirebaseUser?> {
        return try {
            val result = auth.signInWithEmailAndPassword(email, password)
            Result.success(result.user)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Send password reset email
     * @return Result with Unit on success or Exception on failure
     */
    suspend fun sendPasswordResetEmail(email: String): Result<Unit> {
        return try {
            auth.sendPasswordResetEmail(email)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Logout current user
     */
    suspend fun logout() {
        auth.signOut()
    }

    /**
     * Get current logged in user
     * @return FirebaseUser if logged in, null otherwise
     */
    fun getCurrentUser(): FirebaseUser? {
        return try { auth.currentUser } catch (e: Exception) { null }
    }

    fun isLoggedIn(): Boolean {
        return try { auth.currentUser != null } catch (e: Exception) { false }
    }

    fun getCurrentUserId(): String? {
        return try { auth.currentUser?.uid } catch (e: Exception) { null }
    }

    fun getCurrentUserEmail(): String? {
        return try { auth.currentUser?.email } catch (e: Exception) { null }
    }

    /**
     * Delete the current Firebase Auth user account.
     * Note: Firebase requires the user to have signed in recently.
     * If this fails with "requires-recent-login", the user must sign in again.
     * @return Result with Unit on success or Exception on failure
     */
    suspend fun deleteAccount(): Result<Unit> {
        return try {
            val user = auth.currentUser ?: throw Exception("No user logged in")
            // Sign out first to clear any cached credentials
            user.delete()
            auth.signOut()
            Result.success(Unit)
        } catch (e: Exception) {
            println("AuthRepository: deleteAccount failed — ${e.message}")
            // Even if delete fails, sign out the user
            try { auth.signOut() } catch (_: Exception) {}
            Result.failure(e)
        }
    }
}


